import { Pages } from 'modules/navigation/domain/enums/pages';
import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';
import { Player } from 'modules/players/domain/interfaces/player';
import { addTournamentUserToTeam } from 'modules/players/store/actions';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { SingleValue } from 'react-select';
import AsyncSelect from 'react-select/async';
import { toast } from 'react-toastify';

import { AppDispatch } from 'store/store';
import { UDButton, UDText } from 'ud-ui/components';
import Modal from 'ud-ui/components/modal';
import { loadOptions } from './helpers/loadOptions';
import { Info, Link, LoadingMessage, SelectComponent } from './styles';

type PlayerOption = {
  label: React.ReactNode;
  value: Player;
};

/** Пауза в наборе, после которой ищем игроков: AsyncSelect зовёт loadOptions на каждый ввод. */
const SEARCH_DEBOUNCE_MS = 350;

interface IProps {
  teamId: number;
}

function useModalAddPlayer({ teamId }: IProps) {
  const dispatch = useDispatch<AppDispatch>();
  const { info } = useOrganizer();

  const [selectedOptions, setSelectedOptions] = useState<PlayerOption | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const open = useCallback(() => {
    setIsOpen(true);
  }, []);
  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleClose = useCallback(() => {
    setSelectedOptions(null);
    close();
  }, [close]);

  const selectedPlayer = selectedOptions?.value;

  const tournamentId = info?.id;
  const handleLoadOptions = useMemo(() => {
    let timer: ReturnType<typeof setTimeout>;

    return (inputValue: string) =>
      new Promise<PlayerOption[]>((resolve) => {
        clearTimeout(timer);
        timer = setTimeout(() => {
          resolve(tournamentId ? loadOptions(inputValue, tournamentId) : []);
        }, SEARCH_DEBOUNCE_MS);
      });
  }, [tournamentId]);

  const handleAddPlayer = useCallback(async () => {
    if (!selectedPlayer) return;

    try {
      await dispatch(addTournamentUserToTeam({ userId: selectedPlayer.id, teamId })).unwrap();
      // Состав команды перечитает getTeamById — thunk сбрасывает кэш по тегу Team.
      handleClose();
      toast.success('Игрок добавлен в команду');
    } catch (error) {
      if (error === 'User is already in this team') {
        toast.warn('Игрок уже в команде');
      } else {
        toast.error('Ошибка при добавлении игрока в команду');
      }
    }
  }, [dispatch, handleClose, selectedPlayer, teamId]);

  const handleSelectPlayer = useCallback((newValue: SingleValue<PlayerOption>) => {
    setSelectedOptions(newValue);
  }, []);

  const modal = (
    <Modal show={isOpen} onClose={handleClose}>
      <UDText type="title-3" className="mb-12.5">
        Добавить игрока из списка
      </UDText>
      <Modal.Body>
        <AsyncSelect
          key={isOpen ? 'open' : 'closed'}
          autoFocus
          cacheOptions
          loadOptions={handleLoadOptions}
          components={SelectComponent}
          loadingMessage={LoadingMessage}
          placeholder="Введите ФИО игрока"
          className="mb-7.5"
          onChange={handleSelectPlayer}
          value={selectedOptions}
        />
        <Info player={selectedPlayer} />
      </Modal.Body>
      <Modal.Footer className="flex flex-col">
        <UDButton type="button" className="w-4/5" variant="primary" onClick={handleAddPlayer} disabled={!selectedPlayer}>
          Добавить
        </UDButton>
        <Link to={Pages.TEAMS.builders.edit(teamId, 'players/create')}>Создать нового игрока</Link>
      </Modal.Footer>
    </Modal>
  );

  return { open, Modal: modal };
}

export default useModalAddPlayer;
